import { useEffect, useRef } from "react";
import { useSelector, RootStateOrAny } from "react-redux";
import { toast } from "react-toastify";
import { UserState } from "../interfaces";
import {
  checkAndRefreshTokenIfNeeded,
  needsTokenRefresh,
} from "./useRefreshToken";

// Constantes
const INITIAL_CHECK_DELAY = 3000; // 3 segundos após inicialização
const PERIODIC_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutos
const TOKEN_CHECK_SESSION_KEY = "@token-health-check-done";

/**
 * Hook para monitorar a saúde do token de autenticação
 *
 * Funcionalidades:
 * 1. Verificação inicial do token após o carregamento da aplicação
 * 2. Verificações periódicas para renovação automática
 * 3. Renovação proativa antes da expiração
 */
export const useTokenHealth = () => {
  const { signed } = useSelector<RootStateOrAny, UserState>(
    (state) => state.user
  );

  // Referência para intervalo de verificação periódica
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Função para verificar e renovar o token
  const verifyAndRefreshToken = async () => {
    try {
      // Só verificar quando usuário estiver logado
      if (!signed) return;

      // Verificar se o token precisa ser renovado
      if (needsTokenRefresh()) {
        console.log(
          "[TokenHealth] Token precisa ser renovado, iniciando refresh"
        );

        const refreshSuccess = await checkAndRefreshTokenIfNeeded();

        if (refreshSuccess) {
          console.log("[TokenHealth] Token renovado com sucesso");
        } else {
          console.warn("[TokenHealth] Falha na renovação do token");
          // Não deslogamos aqui, o interceptor do axios tratará isso se necessário
        }
      } else {
        console.log("[TokenHealth] Token não precisa ser renovado");
      }
    } catch (error) {
      console.error("[TokenHealth] Erro na verificação do token:", error);
    }
  };

  // Efeito para verificação inicial e configuração de verificações periódicas
  useEffect(() => {
    // Se usuário não estiver logado, não precisa verificar token
    if (!signed) return undefined;

    // Verificação inicial com atraso para garantir carregamento completo
    const initialCheckFlag = sessionStorage.getItem(TOKEN_CHECK_SESSION_KEY);

    // Só realiza verificação inicial uma vez por sessão
    if (!initialCheckFlag) {
      const initialCheckTimeout = setTimeout(() => {
        verifyAndRefreshToken();
        sessionStorage.setItem(TOKEN_CHECK_SESSION_KEY, "true");
      }, INITIAL_CHECK_DELAY);

      // Cleanup do timeout inicial
      return () => clearTimeout(initialCheckTimeout);
    }

    // Configurar verificações periódicas
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
    }

    tokenCheckIntervalRef.current = setInterval(() => {
      verifyAndRefreshToken();
    }, PERIODIC_CHECK_INTERVAL);

    // Cleanup do intervalo na desmontagem
    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
        tokenCheckIntervalRef.current = null;
      }
    };
  }, [signed]);

  // Não retorna nada, é um efeito colateral
  return null;
};

export default useTokenHealth;
